import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { HttpLoaderComponent } from '@core-components';
import { TranslatedRouter, TranslationService } from '@core-services';
import { FooterComponent } from '@shared-components';
import { Language, Path } from '@shared-enums';
import { BasicSeo } from '@shared-models';
import { SeoHttpService, SeoUtilityService } from '@shared-services';
import { filter, of, switchMap } from 'rxjs';
import { PaymentSuccessfulComponent } from './features/payment/components';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    HttpLoaderComponent,
    PaymentSuccessfulComponent,
    FooterComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  //#region Dependencies

  private seoHttpService = inject(SeoHttpService);
  private router = inject(Router);
  private translationService = inject(TranslationService);
  private translatedRouter = inject(TranslatedRouter);
  private seoUtilityService = inject(SeoUtilityService);
  private document = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef);

  //#endregion

  //#region Lifecycle Hooks

  ngOnInit(): void {
    this.initSeo();
  }

  //#endregion

  //#region Init

  private initSeo(): void {
    this.seoHttpService
      .getAll()
      .pipe(
        switchMap((seoItems: BasicSeo[]) =>
          this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            switchMap(event => {
              const navigation = event as NavigationEnd;
              const url = navigation.urlAfterRedirects || navigation.url;
              return this.translationService.language$.pipe(
                switchMap(lang => {
                  this.setDocumentLang(lang);
                  this.setAllMetaTags(
                    lang,
                    seoItems,
                    url === Path.Root ? Path.Root : url.slice(1),
                  );
                  return of(null);
                }),
                takeUntilDestroyed(this.destroyRef),
              );
            }),
            takeUntilDestroyed(this.destroyRef),
          ),
        ),
      )
      .subscribe();
  }

  //#endregion

  //#region Utilities

  private setDocumentLang(lang: Language): void {
    this.document.documentElement.lang = lang;
  }

  setAllMetaTags(lang: Language, seoItems: BasicSeo[], url: string): void {
    const seo = seoItems.find(entry => entry.url[lang] === url);
    const otherLangSeo = seoItems.find(x => Object.values(x.url).includes(url));
    if (seo) {
      this.seoUtilityService.setMetaTags(seo, lang);
    } else if (otherLangSeo) {
      this.translatedRouter.navigateByUrl(otherLangSeo.url[lang]);
    }
  }

  //#endregion
}
